import React from 'react';
import { Helmet } from 'react-helmet-async';
import { locationConfigs } from '@/data/locationData';

interface PageProps {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  canonicalUrl?: string;
  image?: string;
}

export const SEO: React.FC<PageProps> = ({ title, description, children, canonicalUrl, image }) => {
  const defaultTitle = "Y3 Smiles Dental - Your Trusted Local Dentist";
  const defaultDescription = "Welcome to Y3 Smiles Dental. We offer a wide range of dental services from general checkups to cosmetic dentistry. Book your appointment today!";

  // Canonical site base used for structured data and fallbacks
  const siteBase = 'https://y3smilesdental.com.au';

  const OG_IMAGE_VERSION = '2026-05-07'; // bump when og:images are swapped
  const DEFAULT_OG_IMAGE = `/clinic-pics/large/Y3%20-%20Photo-119.jpg`; // shopfront — verified live

  // Only emit canonical when explicitly provided (resolved to full URL)
  const resolvedCanonical = canonicalUrl
    ? canonicalUrl.startsWith('http')
      ? canonicalUrl
      : `${siteBase}${canonicalUrl.startsWith('/') ? canonicalUrl : `/${canonicalUrl}`}`
    : undefined;

  // LocalBusiness (Dentist) JSON-LD structured data (AU-focused)
  const primary = locationConfigs?.broadmeadows?.locationData;
  const opening = (locationConfigs?.broadmeadows?.openingHours || []).map((h) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: h.day, // schema.org allows full day names
    opens: h.hours.includes('Closed') ? undefined : h.hours.split(' - ')[0]?.replace(' ', ''),
    closes: h.hours.includes('Closed') ? undefined : h.hours.split(' - ')[1]?.replace(' ', ''),
  }));

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Dentist',
    name: 'Y3 Smiles Dental',
    url: siteBase,
    telephone: '+61390224442',
    priceRange: '$$',
    description: 'Y3 Smiles Dental in Broadmeadows offers general, cosmetic, and emergency dentistry for the whole family. CDBS accepted, NIB First Choice provider, open 6 days.',
    logo: `${siteBase}/logo.svg`,
    image: [
      `${siteBase}/clinic-pics/large/Y3%20-%20Photo-119.jpg`,
      `${siteBase}/clinic-pics/large/Y3%20-%20Photo-95.jpg`,
      `${siteBase}/clinic-pics/large/Y3%20-%20Photo-6.jpg`,
      `${siteBase}/clinic-pics/large/Y3%20-%20Photo-18.jpg`,
      `${siteBase}/clinic-pics/large/Y3%20-%20Photo-22.jpg`,
      `${siteBase}/clinic-pics/large/Y3%20-%20Photo-45.jpg`,
      `${siteBase}/clinic-pics/large/Y3%20-%20Photo-52.jpg`,
      `${siteBase}/clinic-pics/large/Y3%20-%20Photo-63.jpg`,
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5.0',
      reviewCount: '39',
      bestRating: '5',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Unit 1, 34 King William St',
      addressLocality: 'Broadmeadows',
      addressRegion: 'VIC',
      postalCode: '3047',
      addressCountry: 'AU',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -37.6790611,
      longitude: 144.8419854,
    },
    hasMap: primary?.mapUrl || 'https://maps.app.goo.gl/XCeiAYziTDqGdJpc8',
    sameAs: [
      'https://www.facebook.com/people/Y3-Smiles-Dental/61579053037448/',
      'https://www.instagram.com/y3_smilesdental/',
    ],
    areaServed: [
      'Broadmeadows',
      'Glenroy',
      'Greenvale',
      'Campbellfield',
      'Meadow Heights',
      'Roxburgh Park',
      'Craigieburn',
      'Westmeadows',
      'Mickleham',
      'Fawkner',
      'Gladstone Park',
      'Hadfield',
      'Dallas',
      'Jacana',
      'Coolaroo',
      'Oak Park',
      'Pascoe Vale',
      'Pascoe Vale South',
      'Strathmore',
      'Strathmore Heights',
      'Gowanbrae',
      'Tullamarine',
      'Airport West',
      'Niddrie',
      'Essendon North',
      'Essendon Fields',
      'Reservoir',
      'Coburg North',
      'Coburg',
      'Brunswick West',
      'Brunswick',
      'Thornbury',
      'Preston',
      'Lalor',
      'Thomastown',
      'Bundoora',
      'Greensborough',
      'Viewbank',
      'Heidelberg West',
      'Heidelberg Heights',
      'Ivanhoe',
      'Ivanhoe East',
      'Yallambie',
      'Macleod',
      'Kalkallo',
      'Attwood',
      'Somerton',
      'Upfield',
    ],
    openingHoursSpecification: opening,
  } as const;

  // Build per-page breadcrumbs from the canonical URL
  function buildBreadcrumbs(canonical: string | undefined): object | null {
    if (!canonical) return null;
    const url = new URL(canonical);
    const segments = url.pathname.split('/').filter(Boolean);
    const items: Array<{ '@type': string; position: number; name: string; item: string }> = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteBase + '/' },
    ];

    const segmentLabels: Record<string, string> = {
      'about': 'About',
      'treatments': 'Treatments',
      'locations': 'Service Areas',
      'service-areas': 'Service Areas',
      'news-and-articles': 'News & Articles',
      'patients': 'Patients',
      'patient-resources': 'Patient Resources',
      'prices': 'Prices',
      'specials': 'Specials',
      'contact': 'Contact',
      'booking': 'Book Appointment',
      'booking-success': 'Booking Confirmed',
      'privacy-policy': 'Privacy Policy',
    };

    let cumulative = siteBase;
    segments.forEach((seg, i) => {
      cumulative += '/' + seg;
      const label = segmentLabels[seg] ?? seg
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      items.push({
        '@type': 'ListItem',
        position: i + 2,
        name: label,
        item: cumulative + '/',
      });
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items,
    };
  }

  // Determine if this is a multilingual page that needs hreflang tags
  const canonicalPath = canonicalUrl
    ? canonicalUrl.replace('https://y3smilesdental.com.au', '').replace('http://y3smilesdental.com.au', '') || '/'
    : null;
  const isMultilingual = canonicalPath === '/' || canonicalPath === '/ar/' || canonicalPath === '/tr/';

  return (
    <Helmet>
      <title>{title || defaultTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content="index, follow" />
      <meta name="msvalidate.01" content="2AC8742F63075C0F3EA2761A35EEB08D" />
      
      {/* Load Google Fonts asynchronously — minimum weights only to reduce payload */}
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&family=Raleway:wght@700&display=swap"
        media="print"
        onLoad={(event) => {
          const target = event.currentTarget as HTMLLinkElement;
          target.media = "all";
        }}
      />
      <noscript>{'<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&family=Raleway:wght@700&display=swap" />'}</noscript>
      
      <meta name="geo.region" content="AU-VIC" />
      <meta name="geo.placename" content="Broadmeadows" />
      <meta name="geo.position" content="-37.6790611;144.8419854" />
      <meta name="ICBM" content="-37.6790611, 144.8419854" />
      <meta property="og:title" content={title || defaultTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Y3 Smiles Dental" />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:image" content={image ? (image.startsWith('http') ? image : `${siteBase}${image}?v=${OG_IMAGE_VERSION}`) : `${siteBase}${DEFAULT_OG_IMAGE}?v=${OG_IMAGE_VERSION}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Y3 Smiles Dental clinic in Broadmeadows" />
      {resolvedCanonical && <meta property="og:url" content={resolvedCanonical} />}
      {resolvedCanonical && <link rel="canonical" href={resolvedCanonical} />}
      {/* hreflang annotations — symmetrical across en/ar/tr */}
      {isMultilingual && <link rel="alternate" hrefLang="en" href="https://y3smilesdental.com.au/" />}
      {isMultilingual && <link rel="alternate" hrefLang="ar" href="https://y3smilesdental.com.au/ar/" />}
      {isMultilingual && <link rel="alternate" hrefLang="tr" href="https://y3smilesdental.com.au/tr/" />}
      {isMultilingual && <link rel="alternate" hrefLang="x-default" href="https://y3smilesdental.com.au/" />}
      {/* Twitter / X Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || defaultTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={image ? (image.startsWith('http') ? image : `${siteBase}${image}?v=${OG_IMAGE_VERSION}`) : `${siteBase}${DEFAULT_OG_IMAGE}?v=${OG_IMAGE_VERSION}`} />
      <meta name="twitter:image:alt" content="Y3 Smiles Dental clinic in Broadmeadows" />
      {/* Favicon - Google compliant (minimum 48x48) */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" href="/favicon-48.png" sizes="48x48" />
      <link rel="icon" type="image/png" href="/favicon-192.png" sizes="192x192" />
      <link rel="icon" type="image/png" href="/favicon-512.png" sizes="512x512" />
      <link rel="apple-touch-icon" href="/favicon-192.png" />
      {/* WebSite JSON-LD — populates site name above URL in search results */}
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Y3 Smiles Dental',
        alternateName: ['Y3 Smiles', 'Y3 Smiles Dental Broadmeadows'],
        url: siteBase,
        publisher: {
          '@type': 'Dentist',
          name: 'Y3 Smiles Dental',
          url: siteBase,
          logo: `${siteBase}/logo.svg`,
        },
      })}</script>
      {/* LocalBusiness JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      {/* BreadcrumbList JSON-LD (per-page, derived from canonical URL) */}
      {(() => {
        const bc = buildBreadcrumbs(resolvedCanonical);
        return bc ? (
          <script type="application/ld+json">{JSON.stringify(bc)}</script>
        ) : null;
      })()}
      {children}
    </Helmet>
  );
};

export default SEO;